import type { CasinoConfigType } from '../../../api/fetchers/casinoConfig/types';
import type { EnvConfigType } from '../../../api/fetchers/env/types';
import type { Compliance, PlayerConfigType } from '../../../api/fetchers/playerConfig/types';
import type { RtpType } from '../../../api/fetchers/rtp/types';
import type { StatsSettingsType } from '../../../api/fetchers/settings/types';
import type { StatsConfigType } from '../../../api/fetchers/stats/types';
import type { Jurisdiction, TableConfigType } from '../../../api/fetchers/tableConfig/types';
import { WinMessageBranding } from '../../../api/fetchers/tableConfig/types';
import type { RealityCheckType } from '../../../api/fetchers/versions/types';
import type { IUserSession } from '../../../config/UserSession';
import { useCoreSelector } from '../../CoreStore';
import type { ILimit } from '../../slices/bets/types';

import { selectShouldMergeBonusBalance } from './rawSelectors';
import type { ICoreTimings, PlayAreaDimensions } from './types';

export const useConfigSlice = {
  casinoConfig: (): CasinoConfigType | null => {
    return useCoreSelector(({ config }) => config.casinoConfig);
  },
  env: (): EnvConfigType | null => {
    return useCoreSelector(({ config }) => config.env);
  },
  playerConfig: (): PlayerConfigType | null => {
    return useCoreSelector(({ config }) => config.playerConfig);
  },
  session: (): IUserSession | null | undefined => {
    return useCoreSelector(({ config }) => config.session);
  },
  tableConfig: (): TableConfigType | null => {
    return useCoreSelector(({ config }) => config.tableConfig);
  },
  statsSettings: (): StatsSettingsType | null => {
    return useCoreSelector(({ config }) => config.statsSettings);
  },
  statsConfig: (): StatsConfigType | null => {
    return useCoreSelector(({ config }) => config.statsConfig);
  },
  baseUrl: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.metaServer ?? config.session?.actualWebServer ?? null);
  },
  urlParams: (): string => {
    return useCoreSelector(({ config }) => config.urlParams);
  },
  realityCheck: (): RealityCheckType | null => {
    return useCoreSelector(({ config }) => config.realityCheck);
  },
  JSESSIONID: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.JSESSIONID ?? null);
  },
  tableId: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.tableId ?? null);
  },
  gameMode: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.gameMode ?? null);
  },
  playerLanguage: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.lang ?? null);
  },
  playAreaSize: (): number => {
    return useCoreSelector(({ config }) => config.playAreaSize);
  },
  playAreaDimensions: (): PlayAreaDimensions | null => {
    return useCoreSelector(({ config }) => config.playAreaDimensions);
  },
  tableBetLimits: (): ILimit | null => {
    return useCoreSelector(({ config }) => config.tableConfig?.tableBetLimit ?? null);
  },
  tableName: (): string | null => {
    return useCoreSelector(({ config }) => config.tableConfig?.tableName ?? null);
  },
  gsAddress: (): string | null => {
    return useCoreSelector(({ config }) => config.tableConfig?.gsAddress ?? null);
  },
  compliance: (): Compliance | null => {
    return useCoreSelector(({ config }) => config.playerConfig?.compliance ?? null);
  },
  getShouldMergeBonusBalance: (): boolean | undefined => {
    return useCoreSelector(selectShouldMergeBonusBalance);
  },
  jurisdiction: (): Jurisdiction | null => {
    return useCoreSelector(({ config }) => config.tableConfig?.jurisdiction ?? null);
  },
  jwtToken: (): string | null | undefined => {
    return useCoreSelector(({ config }) => config.playerConfig?.jwtToken);
  },
  userId: (): string | null => {
    return useCoreSelector(({ config }) => config.playerConfig?.userId ?? null);
  },
  countryCode: (): string | null => {
    return useCoreSelector(({ config }) => config.playerConfig?.countryCode ?? null);
  },
  videoStream: (): boolean | null => {
    return useCoreSelector(({ config }) => config.playerConfig?.videoStream ?? null);
  },
  showOtherMessages: (): boolean => {
    return useCoreSelector(({ config }) => {
      const disableChat = config.tableConfig?.jurisdiction?.disableChat;
      const showOtherMessages = config.playerConfig?.showOtherMessages;
      return Boolean(!disableChat && showOtherMessages);
    });
  },
  version: (): string | null => {
    return useCoreSelector(({ config }) => config.versions?.[0]?.version ?? null);
  },
  getCashierIframeEvent: (): boolean => {
    return useCoreSelector(({ config }) => config.casinoConfig?.cashierIframeEvent ?? false);
  },
  getCashierPopupEnabled: (): boolean => {
    return useCoreSelector(({ config }) => config.casinoConfig?.cashierPopupEnabled ?? false);
  },
  getCashierUrl: (): string | null => {
    return useCoreSelector(({ config }) => config.casinoConfig?.cashierUrl ?? null);
  },
  getCashierUrlFirst: (): string | null => {
    return useCoreSelector(({ config }) => config.casinoConfig?.cashierUrlFirst ?? null);
  },
  getCashierUrlMobile: (): string | null => {
    return useCoreSelector(({ config }) => config.casinoConfig?.cashierUrlMobile ?? null);
  },
  getChipColorCodes: (): string[] | null => {
    return useCoreSelector(({ config }) => config.tableConfig?.chipColorCodes ?? null);
  },
  getRtp: (): RtpType | null => {
    return useCoreSelector(({ config }) => config.rtp);
  },
  getGameType: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.gametype ?? null);
  },
  getMetaServer: (): string | null => {
    return useCoreSelector(({ config }) => config.session?.metaServer ?? null);
  },
  getTimings: (): ICoreTimings | undefined => {
    return useCoreSelector(({ config }) => config.timings);
  },
  getTiming: (key: keyof ICoreTimings): number | undefined => {
    return useCoreSelector(({ config }) => config.timings?.[key]);
  },
  isChatDisabled: (): boolean => {
    return useCoreSelector(
      ({ config }) => (config.casinoConfig?.chatDisabled || config.tableConfig?.chatDisabled) ?? false
    );
  },
  isMicroGamingEnabled: (): boolean => {
    return useCoreSelector(({ config }) => config.casinoConfig?.isMicroGamingEnabled ?? false);
  },
  isResponsibleGamingEnabled: (): boolean => {
    return useCoreSelector(({ config }) => config.casinoConfig?.isResponsibleGamingEnabled ?? false);
  },
  isAsia: (): boolean => {
    return useCoreSelector(({ config }) =>
      config.casinoConfig?.casinoRegion
        ? config.casinoConfig?.casinoRegion?.toLowerCase() === 'asia' ||
          config.casinoConfig?.casinoRegion?.toLowerCase() === 'manila'
        : false
    );
  },
  isSubtitlesAvailable: (): boolean => {
    return useCoreSelector(({ config }) => config.tableConfig?.subtitlesAvailable ?? false);
  },
  isSubtitlesEnabled: (): boolean => {
    return useCoreSelector(({ config, subtitles }) => config.playerConfig?.subtitlesEnabled[subtitles.group] ?? false);
  },
  gameHeaderOverlayHeight: (): number => {
    return useCoreSelector(({ config }) => config.gameHeaderOverlayHeight ?? 0);
  },
  isChatVisibleByDefault: (): boolean => {
    return useCoreSelector(({ config }) => config.tableConfig?.chatVisibleByDefault ?? false);
  },
  isInfiniteAutoPlayEnabled: (): boolean => {
    return useCoreSelector(({ config }) => config.tableConfig?.isInfiniteAutoPlayEnabled ?? false);
  },
  isCanvasEnabled: (): boolean => {
    return useCoreSelector(({ config }) => config.tableConfig?.canvasEnabled ?? false);
  },
  isGameVisible: (): boolean => {
    return useCoreSelector(({ config }) => config.isGameVisible);
  },
  isRestrictNickNameEdit: (): boolean => {
    return useCoreSelector(({ config }) => config.casinoConfig?.restrictNicknameEdit ?? false);
  },
  getTableVariant: (): string => {
    return useCoreSelector(({ config }) => config.tableConfig?.tableVariant ?? '');
  },
  getHasNoCommissionToggle: (): boolean => {
    return useCoreSelector(({ config }) => config.tableConfig?.hasNoCommissionToggle ?? false);
  },
  getWinMessageBranding: (): WinMessageBranding => {
    return useCoreSelector(({ config }) => config.tableConfig?.winMessageBranding ?? WinMessageBranding.NONE);
  },
  getMaxPayout: (): number | null => {
    return useCoreSelector(({ config }) => config.tableConfig?.payoutBetMaxLimit ?? null);
  },
  isSessionTimerVisisble: (): boolean => {
    return useCoreSelector(({ config }) => {
      const showSessionTimer = config.playerConfig?.compliance.showSessionTimer ?? false;
      const hideSessionTimer = config.casinoConfig?.hideSessionTimer ?? false;
      return showSessionTimer && !hideSessionTimer;
    });
  },
};
